import { useConfig, useOidc } from '@revenuewell/front-end-bundle';
import { useEffect, useRef } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { useHistory } from './use-history-intercept';
import { usePubnub } from './use-pubnub/use-pubnub';

const INTERCOM_ELEMENT_IDS = [
  'intercom-container',
  'intercom-frame',
  'intercom-tooltips-container',
  'intercom-css-container'
];

const setIntercomVisibility = (visible: boolean) => {
  INTERCOM_ELEMENT_IDS.forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.style.display = visible ? '' : 'none';
  });
};

const IntercomInit = (): null => {
  const { oidcService } = useOidc();
  const { boot, update, hide } = useIntercom();
  const { mailboxes } = usePubnub();
  const history = useHistory();
  const isMessengerRef = useRef(false);
  const hideRef = useRef(hide);
  hideRef.current = hide;

  useEffect(() => {
    if (!mailboxes.length) return;

    const init = async () => {
      const user = await oidcService.getClaims();
      boot({
        hideDefaultLauncher: true,
        name: `${user.firstName} ${user.lastName}`,
        companies: mailboxes.map((m) => ({
          companyId: m.locationId.toString(),
          name: m.location.name
        }))
      });
    };

    init();
  }, [boot, oidcService, mailboxes]);

  useEffect(() => {
    const isMessenger = history.location.pathname.startsWith('/messenger');
    isMessengerRef.current = isMessenger;
    update({ hideDefaultLauncher: isMessenger });
    if (isMessenger) hide();
    setIntercomVisibility(!isMessenger);
  }, [history.location.pathname, update, hide]);

  useEffect(() => {
    (window as any).Intercom?.('onShow', () => {
      if (!isMessengerRef.current) return;
      hideRef.current();
      setIntercomVisibility(false);
    });
  }, []);

  return null;
};

const useIntercomApi = () => {
  const { trackEvent } = useIntercom();

  const submitEvent = async (eventName: string, eventData?: any) => {
    if (trackEvent) {
      trackEvent(eventName, eventData);
    }
  };

  return {
    submitEvent
  };
};

const Intercom: React.FC<React.PropsWithChildren<any>> = (props) => {
  const { config } = useConfig();

  if (config?.intercom?.appId) {
    return (
      <IntercomProvider appId={config.intercom.appId}>
        <IntercomInit />
        {props.children}
      </IntercomProvider>
    );
  }

  return props.children;
};

export { Intercom, useIntercomApi };
